import {
  resolveCanonicalCategoryKey,
  type CanonicalCategoryKey,
} from "../../lib/apps/categoryNormalization";
import type { RingMetric } from "../../components/screen0-ring/Screen0Ring";
import type { ViewMode } from "../../lib/habits/heatmap";
import type { TranslationStructure } from "../../i18n";
import { localizedCategoryLabel } from "../../lib/apps/categoryIcons";
import type { FocusableTopApp } from "./todayFocusSelection";
import { sortTopAppsForMetric } from "./todayFocusSelection";

export type TodayEntity = FocusableTopApp & {
  /** Namespaced key, e.g. "app:com.foo" or "cat:socialNetworking". */
  entityKey: string;
  isCategory: boolean;
};

export function appEntityKey(app: Pick<FocusableTopApp, "appIdentifier" | "displayName">): string {
  const rawKey = app.appIdentifier?.trim() || app.displayName.trim();
  return `app:${rawKey}`;
}

export function categoryEntityKey(canonicalKey: CanonicalCategoryKey): string {
  return `cat:${canonicalKey}`;
}

/** Localized category label for Today/Habits category entities. */
export function todayEntityLabel(
  entity: Pick<TodayEntity, "category" | "displayName" | "isCategory">,
  t: Pick<TranslationStructure, "categories">,
): string {
  if (!entity.isCategory) return entity.displayName;
  const key = resolveCanonicalCategoryKey(entity.category ?? entity.displayName);
  return localizedCategoryLabel(key, t);
}

/** Rank apps or category buckets for the Today ring and list surfaces. */
export function aggregateTodayEntities(
  apps: FocusableTopApp[],
  viewMode: ViewMode,
  metric: RingMetric,
): TodayEntity[] {
  if (viewMode === "app") {
    return sortTopAppsForMetric(apps, metric).map((app) => ({
      ...app,
      entityKey: appEntityKey(app),
      isCategory: false,
    }));
  }

  const map = new Map<string, TodayEntity>();
  for (const app of apps) {
    const canonicalKey = resolveCanonicalCategoryKey(app.category);
    const key = categoryEntityKey(canonicalKey);
    let entity = map.get(key);
    if (!entity) {
      entity = {
        displayName: canonicalKey,
        category: canonicalKey,
        durationSeconds: 0,
        opens: 0,
        entityKey: key,
        isCategory: true,
      };
      map.set(key, entity);
    }
    entity.durationSeconds += app.durationSeconds;
    entity.opens = (entity.opens ?? 0) + (app.opens ?? 0);
  }

  return sortTopAppsForMetric([...map.values()], metric).map((entity) => ({
    ...entity,
    entityKey: categoryEntityKey(resolveCanonicalCategoryKey(entity.category ?? entity.displayName)),
    isCategory: true as const,
  }));
}

/** @deprecated Zero-usage catalog rows are no longer appended; returns entities unchanged. */
export function mergeTodayCategoryCatalog(entities: TodayEntity[]): TodayEntity[] {
  return entities;
}
